import { Alert, Button, FileTextIcon, ModalDialog } from '@fukuri-admins/ui/common';
import { Checkbox } from '@fukuri-admins/ui/common/Form/FormItems';
import { ROUTES } from '~/utils/routes';
import { MetricRadarCard } from './MetricRadarCard';
import { OverallProgressCard } from './OverallProgressCard';
import styles from './styles.module.scss';

type Props = {
  title: string;
  totalUserCount: number;
  completedUserCount: number;
  completedUserRate: number;
  highStressUserCount: number;
  highStressUserRate: number;
  pendingUserCount: number;
  pendingUserRate: number;
  dataPoints: React.ComponentProps<typeof MetricRadarCard>['dataPoints'];
  showReportGuide: boolean;
  showUnsetBranchUserAlert: boolean;
  unsetBranchUserCount: number;
  onClickReportGuide: React.ComponentProps<typeof Button>['onClick'];
  isDialogOpen: boolean;
  isDontShowAgainChecked: boolean;
  onConfirmDialog: () => void;
  onCancelDialog: () => void;
  onCloseDialog: () => void;
  onChangeDontShowAgain: () => void;
  isLinkageDialogOpen: boolean;
  onConfirmLinkageDialog: () => void;
  onCancelLinkageDialog: () => void;
};

export const StressAssessmentSummaryPresentation: React.FC<Props> = ({
  title,
  totalUserCount,
  completedUserCount,
  completedUserRate,
  highStressUserCount,
  highStressUserRate,
  pendingUserCount,
  pendingUserRate,
  dataPoints,
  showReportGuide,
  showUnsetBranchUserAlert,
  unsetBranchUserCount,
  onClickReportGuide,
  isDialogOpen,
  isDontShowAgainChecked,
  onConfirmDialog,
  onCancelDialog,
  onCloseDialog,
  onChangeDontShowAgain,
  isLinkageDialogOpen,
  onConfirmLinkageDialog,
  onCancelLinkageDialog,
}) => {
  return (
    <div className={styles.container}>
      <div className={styles.titleRow}>
        <h2 className={styles.title}>{title}</h2>
        {showReportGuide && (
          <Button
            variant='primary'
            size='md'
            icon={<FileTextIcon size='xs' />}
            iconPosition='left'
            onClick={onClickReportGuide}
          >
            報告書作成ガイドを確認
          </Button>
        )}
      </div>
      {showUnsetBranchUserAlert && unsetBranchUserCount > 0 && (
        <Alert
          status='error'
          title={`${unsetBranchUserCount}名の利用者に事業所を紐づけしてください`}
          description='ストレスチェックの報告書は事業所単位での作成が必要です。[利用者]メニューから、すべての利用者に事業所を紐づけしてください。'
          action='[利用者]メニューへ'
          actionLink={`${ROUTES.USERS}?unsetBranchUserCount=${unsetBranchUserCount}`}
        />
      )}
      <div className={styles.cardsContainer}>
        <OverallProgressCard
          totalUserCount={totalUserCount}
          completedUserCount={completedUserCount}
          completedUserRate={completedUserRate}
          highStressUserCount={highStressUserCount}
          highStressUserRate={highStressUserRate}
          pendingUserCount={pendingUserCount}
          pendingUserRate={pendingUserRate}
        />
        <MetricRadarCard dataPoints={dataPoints} />
      </div>
      <ModalDialog
        isOpen={isDialogOpen}
        title='事業所を設定しますか？'
        description='ストレスチェックの報告書は事業所単位での作成が必要です。事業所が2箇所以上ある場合、事業所を設定し、利用者に紐づけしたうえで報告書を作成する必要があります。'
        cancelText='事業所を設定する'
        confirmText='このままガイドの確認に進む'
        onClose={onCloseDialog}
        onConfirm={onConfirmDialog}
        onCancel={onCancelDialog}
      >
        <Checkbox
          label='今後、このメッセージを表示しない'
          checked={isDontShowAgainChecked}
          onCheckedChange={onChangeDontShowAgain}
        />
      </ModalDialog>
      <ModalDialog
        isOpen={isLinkageDialogOpen}
        title={`${unsetBranchUserCount}名の利用者に事業所を紐づけしてください`}
        description='ストレスチェックの報告書は事業所単位での作成が必要です。[利用者]メニューから、すべての利用者に事業所を紐づけしてください。'
        cancelText='キャンセル'
        confirmText='利用者に事業所を紐づける'
        onClose={onCancelLinkageDialog}
        onConfirm={onConfirmLinkageDialog}
        onCancel={onCancelLinkageDialog}
      />
    </div>
  );
};
